import {
  checkLmnStatusBelugaReturn,
  fetchService,
  fetchServiceReturn,
  getMerchantLmnInfoReturn,
  LmnMerchantInfoReturn,
} from 'src/helpers/serviceHelpers'
import { ICustomerInfoProps, ICustomerImage } from 'src/helpers/lmnHelpers'
import { MappedPccIcdCodes } from 'src/machines/paymentsDialogMachine'

const PLUGIN_VERSION = 'elements-2.1'

async function getLMNMerchantDetailsByUUID (merchantUuid: string): Promise<LmnMerchantInfoReturn> {
  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v2/lmnmerchants/${merchantUuid}`,
    method: 'GET',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
  })

  return { response, error }
}

async function getMerchantLmnInfo (
  merchantUuid?: string,
): Promise<getMerchantLmnInfoReturn> {
  const params = {
    ...(merchantUuid && {
      uuid: merchantUuid,
    }),
  }

  const queryString = new URLSearchParams(params).toString()

  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/lmn/lmn_form_data?${queryString}`,
    method: 'GET',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
  })

  const {
    name,
    lmn_provider: lmnProvider,
    website,
    modal_logo: modalLogo,
    uuid,
    processor,
  } = response?.merchant as any

  const { response: result, error: errors } = await getLMNMerchantDetailsByUUID(uuid)

  if (errors) {
    console.error('Error occurred while fetching LMN Merchant details:', errors)
  }

  const merchantInfo = {
    merchant_name: name,
    merchant_uuid: uuid,
    merchant_logo: modalLogo,
    merchant_website: website,
    merchant_processor: processor,
    lmn_conditions: result?.lmn_conditions.split(/[{}]+/).filter(Boolean),
    lmn_contraindications: result?.lmn_contraindications.split(/[{}]+/).filter(Boolean),
    lmn_provider: lmnProvider,
    processor,
  }

  return { response: merchantInfo, error }
}

async function checkLmnStatusBeluga ({
  email,
  lmnId,
  merchantUuid,
}: {
  email?: string
  lmnId?: string
  merchantUuid: string
}): Promise<checkLmnStatusBelugaReturn> {
  const params = {
    ...(lmnId && {
      lmn_id: lmnId,
    }),
    ...(email && {
      email,
    }),
    ...(merchantUuid && {
      merchant_uuid: merchantUuid,
    }),
  }

  const queryString = new URLSearchParams(params).toString()

  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/lmn/status?${queryString}`,
    method: 'GET',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
  })

  return { response, error }
}

async function checkLmnPccStatus ({ email, lmnId, productCategoryCodes }: { email?: string, lmnId?: string, productCategoryCodes: string[] }): Promise<checkLmnStatusBelugaReturn> {
  const body: any = { product_category_codes: productCategoryCodes }
  if (lmnId) {
    body.lmnId = lmnId
  } else if (email) {
    body.emailId = email
  }
  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/lmn/lmn-pcc-status-check`,
    method: 'POST',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
    body,
  })
  return { response, error }
}

async function sendOneTimePassword (phone: string): Promise<fetchServiceReturn> {
  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/otp`,
    method: 'POST',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
    body: {
      phone_number: phone,
    },
  })

  return { response, error }
}

async function verifyOneTimePassword (
  phone: string,
  code: string,
): Promise<fetchServiceReturn> {
  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/otp/validate`,
    method: 'POST',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
    body: {
      phone_number: phone,
      one_time_password: code,
    },
  })

  return { response, error }
}

async function submitLmnForm (
  customerInfo: ICustomerInfoProps,
  image: ICustomerImage | null,
  merchantUuid: string,
  groupOrderId?: string,
  isStandaloneLmn?: boolean,
  isLmnRenewal?: boolean,
  isLmnPccFlow?: boolean,
  mappedPccIcdCodes?: MappedPccIcdCodes,
): Promise<fetchServiceReturn> {
  const {
    dateOfBirth,
    addressLine1,
    addressLine2,
    postalCode,
    Q1,
    A1,
    Q2,
    A2,
    Q3,
    A3,
    phoneVerificationTimestamp,
    isSmsTelehealthConsentChecked,
    isTelehealthAndPrivacyConsentChecked,
    firstName,
    lastName,
    email,
    phone,
    city,
    state,
    sex,
    selfReportedMeds,
    allergies,
    medicalConditions,
    diagnosis,
  } = customerInfo

  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/lmn/submit`,
    method: 'POST',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
    body: {
      formObj: {
        consentsSigned: isSmsTelehealthConsentChecked && isTelehealthAndPrivacyConsentChecked,
        firstName,
        lastName,
        dob: dateOfBirth,
        phone,
        email,
        address: `${addressLine1} ${addressLine2}`,
        city,
        state,
        zip: postalCode,
        sex,
        selfReportedMeds,
        allergies,
        medicalConditions,
        diagnosis: diagnosis.replace('*', ','),
        questions: [
          {
            question: Q1,
            answer: A1,
          },
          {
            question: Q2,
            answer: A2,
          },
          {
            question: Q3,
            answer: A3,
          },
        ],
      },
      image,
      merchantUuid,
      phoneVerificationTimestamp,
      groupOrderId,
      isStandaloneLmn,
      isLmnRenewal,
      isLmnPccFlow,
      mappedPccIcdCodes,
    },
  })

  // Normalize the backend response structure
  const normalizedResponse = response as any
  if (normalizedResponse && normalizedResponse.statusCode === 200 && normalizedResponse.status === 'success') {
    return { response: normalizedResponse, error: undefined }
  } if (normalizedResponse && (normalizedResponse.status !== 'success' || normalizedResponse.statusCode !== 200)) {
    return { response: undefined, error: { name: 'LMNError', message: normalizedResponse.error || 'Unknown error' } }
  } if (error) {
    return { response: undefined, error }
  }
  return { response: undefined, error: { name: 'LMNError', message: 'Unknown error' } }
}

async function getGroupOrderByID (merchantOrderReference: string, API_KEY: string): Promise<fetchServiceReturn> {
  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v2/orders/${merchantOrderReference}`,
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
    },
  })

  return { response, error }
}

async function getIcdCodesByPcc (pccs: string[]): Promise<{ response: any, error: any }> {
  const params = {
    pcc: pccs.join(','),
  }
  const queryString = new URLSearchParams(params).toString()
  const { response, error } = await fetchService({
    url: `${String(process.env.API_HOST)}/v3/lmn/fetch-icd-codes?${queryString}`,
    method: 'GET',
    headers: {
      'x-sika-plugin': PLUGIN_VERSION,
    },
  })
  return { response, error }
}

export {
  checkLmnStatusBeluga,
  checkLmnPccStatus,
  getMerchantLmnInfo,
  sendOneTimePassword,
  verifyOneTimePassword,
  submitLmnForm,
  getLMNMerchantDetailsByUUID,
  getGroupOrderByID,
  getIcdCodesByPcc,
}
